/**
 * @fileoverview Main routing configuration for KaraoNow.
 * @summary Defines all application routes.
 *
 * @package KaraoNow
 * @module apps/front/src/routes
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { lazy } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import Layout from '../components/Layout/Layout.js'
import ProtectedRoute from './ProtectedRoute.js'

// Critical pages - loaded synchronously (homepage, login, QR code landing)
import HomePage from '../pages/HomePage.js'
import LoginPage from '../pages/LoginPage.js'
import QRCodeLandingPage from '../pages/QRCodeLandingPage.js'

// Lazy-loaded pages - loaded on demand to reduce initial bundle size
const LandingPage = lazy(() => import('../pages/LandingPage.js'))
const AboutPage = lazy(() => import('../pages/AboutPage.js'))
const RegisterPage = lazy(() => import('../pages/RegisterPage.js'))
const SetPasswordPage = lazy(() => import('../pages/SetPasswordPage.js'))
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPasswordPage.js'))
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage.js'))
const ConfirmEmailPage = lazy(() => import('../pages/ConfirmEmailPage.js'))
const SubscriptionPage = lazy(() => import('../pages/organizer/SubscriptionPage.js'))
const SubscriptionSuccessPage = lazy(() => import('../pages/organizer/SubscriptionSuccessPage.js'))
const DashboardOrganizerPage = lazy(() => import('../pages/organizer/DashboardOrganizerPage.js'))
const CreateEventPage = lazy(() => import('../pages/organizer/CreateEventPage.js'))
const VenuesPage = lazy(() => import('../pages/organizer/VenuesPage.js'))
const EventManagementPage = lazy(() => import('../pages/organizer/EventManagementPage.js'))
const OrganizerSettingsPage = lazy(() => import('../pages/organizer/OrganizerSettingsPage.js'))
const SingerEventPage = lazy(() => import('../pages/singer/SingerEventPage.js'))
const SingerDashboardPage = lazy(() => import('../pages/singer/SingerDashboardPage.js'))
const SingerSettingsPage = lazy(() => import('../pages/singer/SingerSettingsPage.js'))
const DataPrivacyPage = lazy(() => import('../pages/DataPrivacyPage.js'))
const CookiesPage = lazy(() => import('../pages/CookiesPage.js'))
const LegalPage = lazy(() => import('../pages/LegalPage.js'))
const PrivacyPage = lazy(() => import('../pages/PrivacyPage.js'))
const TermsPage = lazy(() => import('../pages/TermsPage.js'))
const NotFoundPage = lazy(() => import('../pages/NotFoundPage.js'))
const ErrorPage = lazy(() => import('../pages/ErrorPage.js'))

/**
 * @summary Main application router.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'landing',
        element: <LandingPage />,
      },
      {
        path: 'about',
        element: <AboutPage />,
      },
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'register',
        element: <RegisterPage />,
      },
      {
        path: 'set-password',
        element: <SetPasswordPage />,
      },
      {
        path: 'forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: 'reset-password',
        element: <ResetPasswordPage />,
      },
      {
        path: 'confirm-email/:token',
        element: <ConfirmEmailPage />,
      },
      {
        path: 'subscription',
        element: (
          <ProtectedRoute requiredRole="organizer">
            <SubscriptionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'subscription/success',
        element: (
          <ProtectedRoute requiredRole="organizer">
            <SubscriptionSuccessPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute requiredRole="organizer">
            <DashboardOrganizerPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'events/create',
        element: (
          <ProtectedRoute requiredRole="organizer">
            <CreateEventPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'venues',
        element: (
          <ProtectedRoute requiredRole="organizer">
            <VenuesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'events/:eventId',
        element: (
          <ProtectedRoute requiredRole="organizer">
            <EventManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/organizer',
        element: (
          <ProtectedRoute requiredRole="organizer">
            <OrganizerSettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'event/:token',
        element: <QRCodeLandingPage />,
      },
      {
        path: 'singer',
        element: <Navigate to="/" replace />,
      },
      {
        path: 'singer/:eventId',
        element: <SingerEventPage />,
      },
      {
        path: 'singer/dashboard',
        element: (
          <ProtectedRoute requiredRole="singer">
            <SingerDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'singer/settings',
        element: (
          <ProtectedRoute requiredRole="singer">
            <SingerSettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'data-privacy',
        element: <DataPrivacyPage />,
      },
      {
        path: 'cookies',
        element: <CookiesPage />,
      },
      {
        path: 'legal',
        element: <LegalPage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPage />,
      },
      {
        path: 'terms',
        element: <TermsPage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
])
